import { cn } from '@/lib/utils';

/**
 * Ambient background ported 1:1 from affiliate-torian-market. Two soft
 * radial color washes (primary lemon + sky), a faint blueprint grid, a
 * hero chart silhouette that draws itself on mount, a probability bar
 * row that grows from the bottom and a vignette to anchor the centered
 * content. Floating odds cards appear on desktop only — on Telegram
 * Mini Apps the viewport is mobile-sized so they stay hidden.
 *
 * All animations are `pointer-events-none aria-hidden` and respect
 * `prefers-reduced-motion`.
 */
export function AuthBackground() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {/* Color wash — primary lemon top-right. */}
      <div
        className="absolute -top-[28rem] -right-[20rem] size-[60rem] sm:-top-[32rem] sm:-right-[24rem] sm:size-[72rem]"
        style={{
          background:
            'radial-gradient(circle at 50% 50%, color-mix(in oklab, var(--primary) 32%, transparent) 0%, color-mix(in oklab, var(--primary) 14%, transparent) 35%, transparent 70%)',
        }}
      />
      {/* Color wash — sky bottom-left. */}
      <div
        className="absolute -bottom-[24rem] -left-[20rem] size-[54rem] sm:-bottom-[28rem] sm:-left-[24rem] sm:size-[64rem]"
        style={{
          background:
            'radial-gradient(circle at 50% 50%, color-mix(in oklab, #0ea5e9 28%, transparent) 0%, color-mix(in oklab, #0ea5e9 12%, transparent) 35%, transparent 70%)',
        }}
      />

      <div className="auth-grid absolute inset-0 opacity-50" />

      <HeroChart />
      <ProbabilityBars />

      {/* Floating odds cards — desktop only. */}
      <OddsCard
        className="absolute top-[16%] left-[6%] hidden lg:flex"
        market="Mercado · Cripto"
        outcome="BTC > US$ 100k"
        odds="62%"
        delta="+1,8 p.p."
        positive
        cardDuration="14s"
        cardDelay="0s"
      />
      <OddsCard
        className="absolute right-[6%] bottom-[20%] hidden lg:flex"
        market="Macro · Copom"
        outcome="Selic mantém"
        odds="78%"
        delta="−0,4 p.p."
        cardDuration="17s"
        cardDelay="-3s"
      />

      {/* Vignette — keeps focus on the centered content. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 50% 50%, transparent 35%, color-mix(in oklab, var(--background) 80%, transparent) 100%)',
        }}
      />
    </div>
  );
}

function HeroChart() {
  const path =
    'M 0,220 C 80,200 130,210 180,196 C 240,180 290,200 350,184 C 420,166 470,180 530,162 C 590,144 640,160 700,134 C 760,108 820,122 880,90 C 940,60 1000,76 1060,46 C 1110,22 1150,30 1200,8';
  const pathLen = 1500;
  const dots = [
    { x: 180, y: 196 },
    { x: 350, y: 184 },
    { x: 530, y: 162 },
    { x: 700, y: 134 },
    { x: 880, y: 90 },
    { x: 1060, y: 46 },
  ];

  return (
    <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 opacity-90 mix-blend-screen dark:opacity-100">
      <svg
        viewBox="0 0 1200 300"
        preserveAspectRatio="none"
        className="h-[58vh] w-full sm:h-[62vh] lg:h-[64vh]"
        role="presentation"
      >
        <defs>
          <linearGradient id="auth-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.28" />
            <stop offset="60%" stopColor="var(--primary)" stopOpacity="0.06" />
            <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="auth-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.7" />
            <stop offset="60%" stopColor="var(--primary)" stopOpacity="0.95" />
            <stop offset="100%" stopColor="var(--primary)" stopOpacity="1" />
          </linearGradient>
        </defs>

        {[80, 150, 220].map((y) => (
          <line
            key={y}
            x1="0"
            y1={y}
            x2="1200"
            y2={y}
            stroke="currentColor"
            strokeOpacity="0.08"
            strokeDasharray="2 6"
          />
        ))}

        <path
          className="auth-chart-area"
          d={`${path} L 1200,300 L 0,300 Z`}
          fill="url(#auth-area)"
        />
        <path
          className="auth-chart-line"
          d={path}
          fill="none"
          stroke="url(#auth-line)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ ['--chart-len' as string]: pathLen }}
        />

        {dots.map((d) => (
          <circle
            key={`${d.x}-${d.y}`}
            className="auth-chart-area"
            cx={d.x}
            cy={d.y}
            r={3}
            fill="var(--primary)"
            opacity="0.75"
          />
        ))}
      </svg>
    </div>
  );
}

function ProbabilityBars() {
  const bars = [
    18, 22, 28, 24, 32, 38, 30, 42, 48, 44, 52, 60, 54, 62, 70, 64, 72, 80, 74,
    82, 90, 84, 92, 100,
  ];
  return (
    <div className="absolute inset-x-0 bottom-[10%] flex h-24 items-end justify-center gap-[3px] px-6 opacity-30 sm:bottom-[8%] sm:h-28 sm:gap-1 sm:px-12 lg:h-32 lg:px-20">
      {bars.map((h, i) => (
        <span
          key={i}
          className={cn(
            'auth-bar block w-1 rounded-full sm:w-1.5',
            i < bars.length / 2
              ? 'bg-foreground/30'
              : i < bars.length - 4
                ? 'bg-sky-500/60'
                : 'bg-primary',
          )}
          style={{
            height: `${h}%`,
            ['--bar-delay' as string]: `${0.4 + i * 0.05}s`,
          }}
        />
      ))}
    </div>
  );
}

function OddsCard({
  className,
  market,
  outcome,
  odds,
  delta,
  positive,
  cardDuration,
  cardDelay,
}: {
  className?: string;
  market: string;
  outcome: string;
  odds: string;
  delta: string;
  positive?: boolean;
  cardDuration: string;
  cardDelay: string;
}) {
  return (
    <div
      className={cn('auth-card-drift', className)}
      style={
        {
          '--card-duration': cardDuration,
          '--card-delay': cardDelay,
        } as React.CSSProperties
      }
    >
      <div className="bg-card/70 supports-backdrop-filter:bg-card/40 ring-foreground/10 shadow-foreground/5 flex min-w-[12rem] flex-col gap-2 rounded-2xl p-3 shadow-xl ring-1 backdrop-blur-md">
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground text-[9px] font-semibold tracking-[0.16em] uppercase">
            {market}
          </span>
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold tracking-wide tabular-nums ring-1',
              positive
                ? 'bg-emerald-500/12 text-emerald-700 ring-emerald-500/25 dark:text-emerald-300'
                : 'bg-rose-500/12 text-rose-700 ring-rose-500/25 dark:text-rose-300',
            )}
          >
            {delta}
          </span>
        </div>
        <div className="flex items-end justify-between gap-2">
          <span className="text-foreground line-clamp-1 text-[11px] font-medium">
            {outcome}
          </span>
          <span className="text-foreground text-base font-semibold tabular-nums">
            {odds}
          </span>
        </div>
        <div className="bg-foreground/5 ring-foreground/10 relative h-1 overflow-hidden rounded-full ring-1">
          <span
            className={cn(
              'absolute inset-y-0 left-0 rounded-full',
              positive ? 'bg-primary' : 'bg-rose-500',
            )}
            style={{
              width: odds.includes('%')
                ? odds
                : `${Math.min(100, Math.round((1 / parseFloat(odds.replace(',', '.'))) * 100))}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
}
